import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faMusic, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useMusicPlayer } from './music_player_context';

export default function SimilarTracksPage() {
  const { similarRequest: request, retrySimilarTracks, requestAddTrack: onAdd, allTracks } = useMusicPlayer();
  const navigate = useNavigate();
  const location = useLocation();
  const ownedIds = new Set(allTracks.map((track) => track.video_id));
  const onClose = () => location.state?.from ? navigate(-1) : navigate('/music');
  const [addingIds, setAddingIds] = useState(() => new Set());
  const [addError, setAddError] = useState('');
  useEffect(() => { setAddError(''); }, [request?.seed?.title, request?.seed?.artist]);
  if (!request) return <main className="similar-tracks-page">
    <header className="all-tracks-header">
      <button type="button" className="all-tracks-back" onClick={onClose}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back
      </button>
      <h2>Similar tracks</h2>
    </header>
    <div className="all-tracks-empty">Select a track to find similar songs.</div>
  </main>;

  const { seed, loading, error, response } = request;
  const resolved = new Map((response?.tracks || []).filter((track) => track.video_id)
    .map((track) => [`${track.suggested_title?.toLowerCase()}|${track.suggested_artist?.toLowerCase()}`, track]));
  const rows = (response?.recommendations || []).map((rec) => ({
    title: rec.title,
    artist: rec.artist,
    track: resolved.get(`${rec.title?.toLowerCase()}|${rec.artist?.toLowerCase()}`),
  }));
  // The API resolves videos independently. Keep unmatched song suggestions visible,
  // but never offer playback or Add to Library without a video ID.

  const add = async (track, title, artist) => {
    if (!track?.video_id || addingIds.has(track.video_id)) return;
    setAddingIds((prev) => new Set(prev).add(track.video_id));
    setAddError('');
    try {
      await onAdd({ ...track, title, artist });
    } catch (err) {
      setAddError(`Could not add ${title}. Please try again.`);
    } finally {
      setAddingIds((prev) => {
        const next = new Set(prev);
        next.delete(track.video_id);
        return next;
      });
    }
  };

  return (
    <main className="similar-tracks-page" aria-label="Similar tracks">
      <header className="all-tracks-header">
        <button type="button" className="all-tracks-back" onClick={onClose} aria-label="Back to previous page">
          <FontAwesomeIcon icon={faArrowLeft} /><span>Back</span>
        </button>
        <div><h2>Similar tracks</h2><p>{seed.title} · {seed.artist}</p></div>
        <span className="all-tracks-header-spacer" />
      </header>
      <div className="all-tracks-list">
        {loading && <div className="all-tracks-empty" role="status">Finding similar tracks…</div>}
        {!loading && error && <div className="all-tracks-empty is-error" role="alert">
          {error} <button type="button" onClick={retrySimilarTracks}>Try again</button>
        </div>}
        {addError && <div className="all-tracks-empty is-error" role="alert">{addError}</div>}
        {!loading && !error && rows.length === 0 && (
          <div className="all-tracks-empty">No similar tracks found.</div>
        )}
        {!loading && !error && rows.map(({ title, artist, track }, index) => {
          const art = track?.thumbnail_medium || track?.thumbnail_high || track?.thumbnail_default;
          const owned = track && ownedIds.has(track.video_id);
          const adding = track && addingIds.has(track.video_id);
          return <div className="all-tracks-row" key={track?.video_id || `${title}-${artist}-${index}`}>
            <span className="all-tracks-art">
              {art ? <img src={art} alt="" /> : <FontAwesomeIcon icon={faMusic} />}
            </span>
            <span className="all-tracks-meta">
              <span className="all-tracks-title">{title || 'Untitled'}</span>
              <span className="all-tracks-artist">{artist || 'Unknown artist'} · {track ? 'YouTube' : 'Recording unavailable'}</span>
            </span>
            {track && <button type="button" disabled={owned || adding} onClick={() => add(track, title, artist)}
              aria-label={`Add ${title || 'track'} to your library`}>
              {owned ? 'In library' : adding ? 'Adding…' : <><FontAwesomeIcon icon={faPlus} /> Add</>}
            </button>}
          </div>;
        })}
      </div>
    </main>
  );
}
